/**
 * Dezimalrechnung auf Basis von BigInt. Beträge und Mengen werden im gesamten
 * System als String transportiert; jede Rechenoperation läuft exakt und
 * rundet erst auf Anforderung kaufmännisch (halb auf, vom Nullpunkt weg).
 */
const DECIMAL_PATTERN = /^-?\d+(\.\d+)?$/;
export class DecimalFormatError extends Error {
    constructor(value) {
        super(`"${value}" ist keine gültige Dezimalzahl.`);
        this.name = 'DecimalFormatError';
    }
}
export function parseDecimal(value) {
    const trimmed = value.trim();
    if (!DECIMAL_PATTERN.test(trimmed)) {
        throw new DecimalFormatError(value);
    }
    const negative = trimmed.startsWith('-');
    const digits = negative ? trimmed.slice(1) : trimmed;
    const [integerPart = '0', fractionPart = ''] = digits.split('.');
    const units = BigInt(`${integerPart}${fractionPart}`);
    return { units: negative ? -units : units, scale: fractionPart.length };
}
function pow10(exponent) {
    return 10n ** BigInt(exponent);
}
/** Skaliert verlustfrei hoch bzw. rundet beim Herunterskalieren kaufmännisch. */
export function rescale(parts, scale) {
    if (scale === parts.scale) {
        return parts;
    }
    if (scale > parts.scale) {
        return { units: parts.units * pow10(scale - parts.scale), scale };
    }
    const divisor = pow10(parts.scale - scale);
    const quotient = parts.units / divisor;
    const remainder = parts.units % divisor;
    const doubled = remainder < 0n ? -remainder * 2n : remainder * 2n;
    if (doubled >= divisor) {
        return { units: parts.units < 0n ? quotient - 1n : quotient + 1n, scale };
    }
    return { units: quotient, scale };
}
export function formatDecimal(parts) {
    const negative = parts.units < 0n;
    const digits = (negative ? -parts.units : parts.units).toString().padStart(parts.scale + 1, '0');
    const cut = digits.length - parts.scale;
    const integerPart = digits.slice(0, cut);
    const fractionPart = digits.slice(cut);
    const body = parts.scale === 0 ? integerPart : `${integerPart}.${fractionPart}`;
    return negative ? `-${body}` : body;
}
function align(a, b) {
    const scale = Math.max(a.scale, b.scale);
    return { a: rescale(a, scale), b: rescale(b, scale) };
}
/** Rundet kaufmännisch auf die angegebene Nachkommastellenzahl. */
export function roundDecimal(value, scale) {
    return formatDecimal(rescale(parseDecimal(value), scale));
}
export function addDecimal(left, right, scale) {
    const { a, b } = align(parseDecimal(left), parseDecimal(right));
    return formatDecimal(rescale({ units: a.units + b.units, scale: a.scale }, scale));
}
export function subtractDecimal(left, right, scale) {
    const { a, b } = align(parseDecimal(left), parseDecimal(right));
    return formatDecimal(rescale({ units: a.units - b.units, scale: a.scale }, scale));
}
export function multiplyDecimal(left, right, scale) {
    const a = parseDecimal(left);
    const b = parseDecimal(right);
    return formatDecimal(rescale({ units: a.units * b.units, scale: a.scale + b.scale }, scale));
}
export function divideDecimal(left, right, scale) {
    const a = parseDecimal(left);
    const b = parseDecimal(right);
    if (b.units === 0n) {
        throw new RangeError('Division durch null ist nicht zulässig.');
    }
    // Zwei Stellen Reserve, damit die abschließende Rundung korrekt ausfällt.
    const workingScale = scale + 2;
    const exponent = workingScale + b.scale - a.scale;
    const numerator = exponent >= 0 ? a.units * pow10(exponent) : a.units;
    const denominator = exponent >= 0 ? b.units : b.units * pow10(-exponent);
    return formatDecimal(rescale({ units: numerator / denominator, scale: workingScale }, scale));
}
export function compareDecimal(left, right) {
    const { a, b } = align(parseDecimal(left), parseDecimal(right));
    if (a.units < b.units) {
        return -1;
    }
    return a.units > b.units ? 1 : 0;
}
export function isNegative(value) {
    return parseDecimal(value).units < 0n;
}
export function isZero(value) {
    return parseDecimal(value).units === 0n;
}
export function negateDecimal(value) {
    const parts = parseDecimal(value);
    return formatDecimal({ units: -parts.units, scale: parts.scale });
}
export function absDecimal(value) {
    const parts = parseDecimal(value);
    return formatDecimal({
        units: parts.units < 0n ? -parts.units : parts.units,
        scale: parts.scale,
    });
}
/** Sum quotients as exact rational decimals; round once after aggregation. */
export function sumDecimalQuotients(terms, scale) {
    const gcd = (a, b) => {
        while (b !== 0n) {
            const remainder = a % b;
            a = b;
            b = remainder;
        }
        return a < 0n ? -a : a;
    };
    let numerator = 0n;
    let denominator = 1n;
    for (const term of terms) {
        const a = parseDecimal(term.numerator);
        const b = parseDecimal(term.denominator);
        if (b.units === 0n)
            throw new RangeError('Division by zero');
        const n = a.units * pow10(b.scale);
        const d = b.units * pow10(a.scale);
        numerator = numerator * d + n * denominator;
        denominator *= d;
        const divisor = gcd(numerator, denominator);
        numerator /= divisor;
        denominator /= divisor;
    }
    return divideDecimal(numerator.toString(), denominator.toString(), scale);
}
//# sourceMappingURL=decimal.js.map